/** Below this we assume a private/incognito context or a nearly full device. */
export const RESTRICTED_QUOTA_BYTES = 100 * 1024 * 1024;

export type StorageHealth = "ok" | "restricted" | "unknown";

/**
 * Best-effort only. There is no official private-mode API and browser vendors
 * deliberately make detection unreliable, so a "restricted" result drives an
 * advisory banner and a persistence fallback — never a block. The durable fix is
 * telling volunteers not to use private browsing in the field.
 */
export const estimateStorageHealth = async (): Promise<StorageHealth> => {
  const storage = navigator.storage as StorageManager | undefined;
  if (!storage?.estimate) return "unknown";
  try {
    const { quota } = await storage.estimate();
    if (typeof quota !== "number") return "unknown";
    return quota < RESTRICTED_QUOTA_BYTES ? "restricted" : "ok";
  } catch {
    return "unknown";
  }
};

/**
 * iOS Safari never fires `beforeinstallprompt`, so an install nudge has to be
 * hand-rolled and gated on the app not already running from the home screen.
 */
export const isIosSafariUninstalled = (): boolean => {
  const ua = window.navigator.userAgent;
  const isIos = /iPad|iPhone|iPod/.test(ua);
  const isStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
  return isIos && !isStandalone;
};

/**
 * Asks the browser not to evict our origin under disk pressure. Feature-detected:
 * broadly available on Chrome/Firefox/Edge, and on Safari only from 17 / iOS 17.
 * Reduces eviction risk; never eliminates it.
 */
export const requestPersistentStorage = async (): Promise<boolean> => {
  const storage = navigator.storage as StorageManager | undefined;
  if (!storage?.persist) return false;
  try {
    return await storage.persist();
  } catch {
    return false;
  }
};
